import React, { createContext, useReducer, useContext, Dispatch } from "react";
import toast from "react-hot-toast";
import { getDelegateCalendarAppointments } from "services/delegateUserService";

export interface DelegateAppointment {
  id?: number; // Id
  type: string; // appointments.Type
  time: number; // appointments.Time
  date: string; // appointments.Date
  bookedByRep: number | null; // appointments.BookedByRep (can be null)
  clientId: number; // appointments.ClientId
  confirmation: number | null; // appointments.Confirmation
  bookedByRepFirstName: string | null; // user_profiles.FirstName
  bookedByRepLastName: string | null; // user_profiles.LastName
  annotationColor: string | null; // appointment_annotations.apptColor
  annotationTitle: string | null; // appointment_annotations.apptTitle
  officeName: string | null; // company_profile.Name
}

interface DelegateUserState {
  appointments: DelegateAppointment[];
  lastFetchedAt: number | null;
  isLoading: boolean;
}

type Action =
  | { type: "SET_APPOINTMENTS"; payload: DelegateAppointment[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "RESET" };

const initialState: DelegateUserState = {
  appointments: [],
  lastFetchedAt: null,
  isLoading: false,
};

interface FetchDelegateAppointmentsParams {
  from: string;
  to: string;
}

const DelegateUserContext = createContext<{
  state: DelegateUserState;
  dispatch: Dispatch<Action>;
  fetchDelegateAppointments: (
    params: FetchDelegateAppointmentsParams,
    refetch?: boolean
  ) => Promise<void>;
} | null>(null);

function reducer(state: DelegateUserState, action: Action): DelegateUserState {
  switch (action.type) {
    case "SET_APPOINTMENTS":
      return {
        ...state,
        appointments: action.payload,
        lastFetchedAt: Date.now(),
        isLoading: false,
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

export const DelegateUserProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchDelegateAppointments = async (
    { from, to }: FetchDelegateAppointmentsParams,
    refetch = false
  ) => {
    try {
      const TEN_MINUTES = 10 * 60 * 1000;
      const shouldRefetch =
        refetch ||
        !state.lastFetchedAt ||
        Date.now() - state.lastFetchedAt > TEN_MINUTES;

      if (!shouldRefetch) return;

      dispatch({ type: "SET_LOADING", payload: true });

      const response = await getDelegateCalendarAppointments({ from, to });
      if (response?.error) {
        toast.error(response.error || "Failed to load delegate appointments");
        return;
      }

      dispatch({
        type: "SET_APPOINTMENTS",
        payload: response?.appointments || [],
      });
    } catch (error) {
      console.error("Error fetching delegate appointments:", error);
      toast.error("Error fetching delegate appointments");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  return (
    <DelegateUserContext.Provider
      value={{ state, dispatch, fetchDelegateAppointments }}
    >
      {children}
    </DelegateUserContext.Provider>
  );
};

export const useDelegateUserContext = () => {
  const context = useContext(DelegateUserContext);
  if (!context) {
    throw new Error(
      "useDelegateUserContext must be used within DelegateUserProvider"
    );
  }
  return context;
};
